/**
 * Usage Progress Component
 * Displays usage statistics with a progress bar
 */

import React from 'react';

interface UsageProgressProps {
  label: string;
  used: number;
  limit: number | null;
  icon: React.ReactNode;
}

export const UsageProgress: React.FC<UsageProgressProps> = ({
  label,
  used,
  limit,
  icon,
}) => {
  const percentage = limit ? Math.min((used / limit) * 100, 100) : 100;
  const isUnlimited = limit === null;
  const remaining = limit !== null ? Math.max(limit - used, 0) : null;
  const progressTone =
    percentage >= 90
      ? 'bg-rose-500'
      : percentage >= 75
        ? 'bg-amber-500'
        : 'bg-emerald-500';

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white/80 p-3">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50">
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-slate-900">{label}</p>
              <p className="text-xs text-slate-500">
                {isUnlimited
                  ? 'Unlimited usage on this plan'
                  : `${remaining} remaining this cycle`}
              </p>
            </div>
            <span className="text-sm font-medium text-slate-500">
              {used} / {isUnlimited ? 'Unlimited' : limit}
            </span>
          </div>

          <div className="mt-3 h-2 rounded-full bg-slate-100">
            <div
              className={`h-2 rounded-full transition-all ${progressTone}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
